import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import type { Nudge } from '../types';

interface NudgesTableProps {
	nudges: Nudge[];
	/** The nudge whose status write is in flight; its switch is held until it settles. */
	pendingNudgeId: string | null;
	onOpen: (nudge: Nudge) => void;
	onToggle: (nudge: Nudge) => void;
	onEdit: (nudge: Nudge) => void;
	onDelete: (nudge: Nudge) => void;
}

interface NudgeRowProps extends Omit<NudgesTableProps, 'nudges'> {
	nudge: Nudge;
}

/**
 * The row is not itself a button: it holds a switch and a menu, and controls nested inside a
 * button are unreachable to assistive tech. The name cell is the button that opens the panel.
 */
const NudgeRow = ({ nudge, pendingNudgeId, onOpen, onToggle, onEdit, onDelete }: NudgeRowProps) => (
	<div className='grid h-12 grid-cols-[minmax(0,1fr)_auto_64px] items-center border-b border-gray-300 last:border-b-0'>
		<button
			type='button'
			onClick={() => onOpen(nudge)}
			aria-label={`Open ${nudge.name}`}
			className='h-full min-w-0 cursor-pointer truncate pl-0.5 text-left text-[13px] leading-[18px] text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
		>
			{nudge.name}
		</button>

		<div className='flex items-center gap-2.5'>
			<span aria-hidden className='h-[18px] w-px bg-gray-300' />
			<Switch
				checked={nudge.status === 'enabled'}
				disabled={pendingNudgeId === nudge.id}
				aria-label={`Enable ${nudge.name}`}
				onCheckedChange={() => onToggle(nudge)}
			/>
		</div>

		<div className='flex justify-end pr-1'>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button variant='ghost' size='icon-sm' aria-label={`Actions for ${nudge.name}`} className='text-gray-900'>
						<MoreHorizontal className='size-4' />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align='end'>
					<DropdownMenuItem className='text-[13px]' onSelect={() => onEdit(nudge)}>
						<Pencil className='size-4' /> Edit
					</DropdownMenuItem>
					<DropdownMenuItem variant='destructive' className='text-[13px]' onSelect={() => onDelete(nudge)}>
						<Trash2 className='size-4' /> Delete
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	</div>
);

/** The nudges, in the server's priority order: the order they are checked in on the store. */
export const NudgesTable = ({ nudges, ...rowProps }: NudgesTableProps) => (
	<div className='flex flex-col'>
		<div className='flex h-10 items-center border-b border-gray-300 px-4 text-xs text-gray-800'>Name</div>
		<div className='flex flex-col px-4'>
			{nudges.map((nudge) => (
				<NudgeRow key={nudge.id} nudge={nudge} {...rowProps} />
			))}
		</div>
	</div>
);
